import clsx from "clsx";
import { debounce } from "lodash";
import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  isSearchboxPausedSF,
  KeyboardKeys,
  noop,
  SEARCH_TYPING_DEBOUNCE_MS,
} from "@ryde/shared";

import { AdornmentButton, AdornmentLensButton } from "../adornments";
import {
  FilterSearchSearchClearIcon,
  FilterSearchStyled,
} from "./filter-search.styled";

interface IFilterSearchProps {
  searchValue: string;
  isError?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  onChange?: (searchValue: string) => void;
  onSearchApply?: () => void;
  applySearchTimeout?: number;
}

const APPLY_SEARCH_TIMEOUT = 20;

export const FilterSearch: FC<IFilterSearchProps> = ({
  searchValue,
  isError,
  placeholder,
  fullWidth,
  onChange,
  onSearchApply,
  applySearchTimeout = APPLY_SEARCH_TIMEOUT,
}) => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchPrevValueRef = useRef<string>(searchValue);
  const isSearchBoxPaused = useRecoilValue(isSearchboxPausedSF(entity));

  const [searchValueInner, setSearchValueInner] = useState(searchValue);

  const isBorderHighlighted =
    !isError && Boolean(searchValue?.trim()) && !isSearchBoxPaused;

  const throttledOnChange = useMemo(
    () =>
      debounce((value: string) => {
        (onChange ?? noop)(value);
      }, SEARCH_TYPING_DEBOUNCE_MS),
    [onChange],
  );

  useEffect(() => {
    setSearchValueInner(searchValue);
  }, [searchValue]);

  const handleFocus = (): void => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const searchInputValue = event.currentTarget?.value;
    setSearchValueInner(searchInputValue);
    throttledOnChange(searchInputValue || "");
  };

  const searchApply = (): void => {
    throttledOnChange.flush();
    setTimeout(() => {
      onSearchApply?.();
      handleFocus();
    }, applySearchTimeout);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }

    searchApply();
  };

  const clearSearch = useEvent((): void => {
    throttledOnChange.cancel();
    setSearchValueInner("");
    onChange?.("");
    setTimeout(() => {
      onSearchApply?.();
      handleFocus();
    }, applySearchTimeout);
  });

  useEffect(() => {
    return () => {
      throttledOnChange.cancel();
    };
  }, [throttledOnChange]);

  useEffect(() => {
    if (searchPrevValueRef?.current?.length) {
      if (!searchValue && searchValueInner?.length) {
        clearSearch();
      }
    }

    searchPrevValueRef.current = searchValue;
  }, [clearSearch, searchValue, searchValueInner?.length]);

  return (
    <FilterSearchStyled
      className={clsx({
        "filter-border__highlighted": isBorderHighlighted,
        "filter-search_border-highlighted": isBorderHighlighted,
        "filter-border__suspended": isSearchBoxPaused,
      })}
      error={isError}
      color="secondary"
      size="small"
      fullWidth={fullWidth}
      placeholder={placeholder ?? t(`general.SEARCH`)}
      value={searchValueInner}
      onKeyDown={onKeyDown}
      onChange={onChangeHandler}
      startAdornment={
        <AdornmentLensButton
          position="start"
          onClick={searchApply}
          buttonProps={{
            color: isError ? "error" : "inherit",
          }}
        />
      }
      endAdornment={
        <AdornmentButton
          position="end"
          onClick={clearSearch}
          hidden={!searchValueInner.length}
          buttonProps={{
            color: isError ? "error" : "inherit",
          }}
        >
          <FilterSearchSearchClearIcon />
        </AdornmentButton>
      }
    />
  );
};
